<script setup>
import { ref, computed, onMounted, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import camelcaseKeys from 'camelcase-keys';
import ContactAPI from 'dashboard/api/contacts';
import CompanyAPI from 'dashboard/api/companies';
import { useDealsStore } from 'dashboard/stores/deals';

import LinkedEntitySection from 'dashboard/components-next/LinkedEntitySection.vue';

// Aba "Contato" / "Empresa" da Negociacao: mesmo visual das abas de Contato
// e Empresa (LinkedEntitySection), gravando o vinculo na propria negociacao.
const props = defineProps({
  deal: { type: Object, required: true },
  kind: {
    type: String,
    required: true,
    validator: v => ['contact', 'company'].includes(v),
  },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();
const dealsStore = useDealsStore();
const openLinkedInPlace = inject('openLinkedInPlace');

const isContact = computed(() => props.kind === 'contact');
const ns = computed(() => `DEALS.SIDEBAR.${props.kind.toUpperCase()}`);
// Contato e unico; empresas sao varias (a principal vem primeiro).
const linkedList = computed(() => {
  if (isContact.value) return props.deal.contact ? [props.deal.contact] : [];
  return props.deal.companies || [];
});

const searchResults = ref([]);
const isSearching = ref(false);
const isUpdating = ref(false);
const selectedCandidate = ref(null);

const doSearch = async query => {
  const q = query.trim();
  isSearching.value = true;
  try {
    const api = isContact.value ? ContactAPI : CompanyAPI;
    const { data } = q
      ? await api.search(q, 1, 'name')
      : await api.get(isContact.value ? 1 : { page: 1, sort: 'name' }, 'name');
    searchResults.value = camelcaseKeys(data?.payload || [], { deep: true });
  } catch {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};
const debouncedSearch = debounce(doSearch, 300);

onMounted(() => doSearch(''));

const subtitleOf = entity =>
  isContact.value
    ? [entity.email, entity.phoneNumber].filter(Boolean).join(' • ')
    : entity.domain;

const options = computed(() =>
  searchResults.value
    .filter(e => !linkedList.value.some(linked => linked.id === e.id))
    .map(e => ({
      value: e.id,
      label: [e.name, subtitleOf(e)].filter(Boolean).join(' · '),
    }))
);

const labels = computed(() => ({
  addTitle: t(`${ns.value}.ADD_TITLE`),
  addDescription: t(`${ns.value}.ADD_DESCRIPTION`),
  searchPlaceholder: t(`${ns.value}.SEARCH_PLACEHOLDER`),
  emptySearch: isSearching.value
    ? t(`${ns.value}.LOADING`)
    : t(`${ns.value}.NO_RESULTS`),
  confirmTitle: t(`${ns.value}.CONFIRM_TITLE`),
  confirmDescription: t(`${ns.value}.CONFIRM_DESCRIPTION`),
  confirm: t(`${ns.value}.CONFIRM_ADD`),
  cancel: t(`${ns.value}.CANCEL`),
  sectionTitle: t(`${ns.value}.TITLE`),
  empty: t(`${ns.value}.EMPTY`),
  open: t(`${ns.value}.OPEN`),
  remove: t(`${ns.value}.REMOVE`),
}));

const items = computed(() =>
  linkedList.value.map(entity => ({
    id: entity.id,
    name: entity.name,
    subtitle: subtitleOf(entity),
    avatarUrl: entity.thumbnail || entity.avatarUrl,
  }))
);

const candidate = computed(() =>
  selectedCandidate.value
    ? {
        name: selectedCandidate.value.name,
        subtitle: subtitleOf(selectedCandidate.value),
        avatarUrl:
          selectedCandidate.value.thumbnail ||
          selectedCandidate.value.avatarUrl,
      }
    : null
);

const handleSelect = id => {
  selectedCandidate.value =
    searchResults.value.find(e => e.id === Number(id)) || null;
};

const clearCandidate = () => {
  selectedCandidate.value = null;
  doSearch('');
};

// Contato: troca o vinculo. Empresa: envia a lista completa, sem perder as demais.
const buildPayload = (action, entityId) => {
  if (isContact.value) {
    return { contact_id: action === 'add' ? entityId : null };
  }
  const ids = linkedList.value.map(c => c.id).filter(id => id !== entityId);
  return { company_ids: action === 'add' ? [...ids, entityId] : ids };
};

const saveLink = async (action, entityId) => {
  if (isUpdating.value) return;
  isUpdating.value = true;
  try {
    const updated = await dealsStore.update({
      id: props.deal.id,
      ...buildPayload(action, entityId),
    });
    emit('updated', updated);
    useAlert(t(`${ns.value}.UPDATE_SUCCESS`));
    selectedCandidate.value = null;
    doSearch('');
  } catch {
    useAlert(t(`${ns.value}.UPDATE_ERROR`));
  } finally {
    isUpdating.value = false;
  }
};
</script>

<template>
  <LinkedEntitySection
    :items="items"
    :candidate="candidate"
    :options="options"
    :labels="labels"
    :is-busy="isUpdating"
    @search="debouncedSearch"
    @select="handleSelect"
    @cancel="clearCandidate"
    @confirm="saveLink('add', selectedCandidate.id)"
    @open="id => openLinkedInPlace(kind, id)"
    @remove="id => saveLink('remove', id)"
  />
</template>
